// The minimap draws the SIM board only. Anything outside it (the dev
// `?seaProto` ring / wall are render-only and never in the WASM minimap, but a
// future sim-published sea frame could add tiles past the declared bounds)
// must not stretch the grid: drop tiles outside the declared q/r box and
// derive the box from the tiles that remain. No tiles => bounds pass through.

import type { MinimapSnapshot, MinimapTile } from '@aod/ui';

export function cropMinimapToBoard(snapshot: MinimapSnapshot): MinimapSnapshot {
  const { minQ, maxQ, minR, maxR } = snapshot;
  const inside = (t: MinimapTile) => t.q >= minQ && t.q <= maxQ && t.r >= minR && t.r <= maxR;
  const tiles = snapshot.tiles.every(inside) ? snapshot.tiles : snapshot.tiles.filter(inside);
  if (tiles.length === 0) return { ...snapshot, tiles };
  let qa = Number.POSITIVE_INFINITY;
  let qb = Number.NEGATIVE_INFINITY;
  let ra = Number.POSITIVE_INFINITY;
  let rb = Number.NEGATIVE_INFINITY;
  for (const t of tiles) {
    if (t.q < qa) qa = t.q;
    if (t.q > qb) qb = t.q;
    if (t.r < ra) ra = t.r;
    if (t.r > rb) rb = t.r;
  }
  if (tiles === snapshot.tiles && qa === minQ && qb === maxQ && ra === minR && rb === maxR) {
    return snapshot;
  }
  return { tiles, minQ: qa, maxQ: qb, minR: ra, maxR: rb };
}

/** The minimap draws the LAND, not the sea: an open board's ocean ring is
 * wide and flat, and it would push the land into a corner of the grid.
 * The crop keeps the land's own box plus one ring, so the coast — and
 * anything standing on it — still shows. Tiles outside the land (the open
 * sea) are dropped; a board with no land is left alone. */
export function cropMinimapToLand(snapshot: MinimapSnapshot): MinimapSnapshot {
  const land = snapshot.tiles.filter((t) => t.biome !== 'ocean');
  if (land.length === 0) return snapshot;
  let minQ = Number.POSITIVE_INFINITY;
  let maxQ = Number.NEGATIVE_INFINITY;
  let minR = Number.POSITIVE_INFINITY;
  let maxR = Number.NEGATIVE_INFINITY;
  for (const t of land) {
    if (t.q < minQ) minQ = t.q;
    if (t.q > maxQ) maxQ = t.q;
    if (t.r < minR) minR = t.r;
    if (t.r > maxR) maxR = t.r;
  }
  // One ring of sea around the land, so the coast is never cut off.
  minQ -= 1;
  maxQ += 1;
  minR -= 1;
  maxR += 1;
  const inside = (t: MinimapTile) => t.q >= minQ && t.q <= maxQ && t.r >= minR && t.r <= maxR;
  const tiles = snapshot.tiles.filter(inside);
  if (tiles.length === 0) return snapshot;
  return { tiles, minQ, maxQ, minR, maxR };
}
